import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { GROUP } from 'utils/rest';

import { Text } from 'components/DataDisplay';
import { Modal } from 'components/Feedback';
import { FormSection } from 'components/FormSection';
import { AutocompleteTextField, Button, TextField } from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { ListField } from 'components/ListField';
import Loading from 'components/Loading';

export const ModalForm = props => {
  const { onValidate, config, setGroups } = props;
  const {
    selectedActivatedAudience = {},
    selectedExtractAudiences = {}
  } = config;

  const [startGroup, setStartGroup] = useState(null);
  const initialState = {
    name: selectedActivatedAudience.name,
    extractId: selectedActivatedAudience.extractId,
    removedGroup: []
  };
  const [{ name, extractId, removedGroup }, setState] = React.useState(
    initialState
  );
  const [extractAudienceOptions, setExtractAudienceOptions] = useState([]);
  const [groupsList, setGroupsList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef(null);

  const removeDuplicates = array => {
    const uniqueId = {};
    return array.filter(item => {
      if (!uniqueId[item.name]) {
        uniqueId[item.name] = true;
        return true;
      }
      return false;
    });
  };

  const handleChange = itemKey => value => {
    const text = value.trim();
    setState(prevState => {
      const states = { ...prevState };
      states[itemKey] = text;
      return states;
    });
  };

  const fetchGroups = async query => {
    const found = await GROUP.getGroupSearch({ search: query });
    if (found?.data) {
      setGroupsList(found.data);
    }
  };

  const pollGroups = query => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      fetchGroups(query);
    }, 500);
  };

  const handleChangeGroup = value => {
    const valueIds = value.map(e => e._id);
    const removed = startGroup.filter(el => !valueIds.includes(el._id));
    setState(prevState => {
      const states = { ...prevState };
      states.removedGroup = removed;
      return states;
    });
    setStartGroup(value);
  };

  const handleChangeExport = (value, reason) => {
    if (reason === 'selectOption') {
      setState(prevState => {
        const states = { ...prevState };
        states.extractId = value._id;
        return states;
      });
    }
  };

  const resetStates = () => {
    setState(initialState);
  };

  const loadData = async () => {
    const fetchedGroups = await GROUP.getGroupSearch({
      activatedAudienceId: selectedActivatedAudience._id
    });
    if (fetchedGroups?.data) {
      setGroupsList(fetchedGroups.data);
    }
    if (fetchedGroups?.audienceGroups) {
      setStartGroup(fetchedGroups.audienceGroups);
      setGroups(fetchedGroups.audienceGroups);
    }
    setExtractAudienceOptions(
      removeDuplicates(
        selectedExtractAudiences.map(({ _id, name: extractName }) => ({
          value: _id,
          _id,
          name: extractName
        }))
      )
    );
    setLoaded(true);
  };

  useEffect(() => {
    if (!loaded) {
      loadData();
    }
    // eslint-disable-next-line
  }, []);

  const doAction = () => {
    const datas = {
      name,
      extractId,
      removedGroup,
      startGroup
    };
    if (!name || name.length === 0 || !extractId || extractId.length === 0) {
      return;
    }
    onValidate(datas);
    resetStates();
  };

  const getActions = () => {
    return (
      <Container>
        <Row spacing={1}>
          <Item xs justify="flex-end">
            <Button
              variant="contained"
              size="medium"
              widthSize="medium"
              onClick={doAction}
            >
              Valider
            </Button>
          </Item>
        </Row>
      </Container>
    );
  };

  return (
    <Modal actions={getActions()} {...props}>
      <Loading loading={!loaded}>
        <Container>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Général">
                <Container>
                  <Row spacing={0}>
                    <Item xs={6} justify="flex-start">
                      <Container>
                        <Row spacing={0} justify="flex-start">
                          <Item xs={11} justify="flex-start">
                            <Text>Export</Text>
                            <AutocompleteTextField
                              options={extractAudienceOptions}
                              required
                              placeholder="Choisissez un export"
                              formatLabel={option =>
                                `${option.name} (${option._id})`
                              }
                              small
                              autoSelect
                              autoHighlight
                              disableClearable
                              value={extractAudienceOptions.find(
                                el => el._id === extractId
                              )}
                              onChange={handleChangeExport}
                            />
                          </Item>
                        </Row>
                      </Container>
                    </Item>
                    <Item xs={6} justify="flex-start">
                      <Container>
                        <Row spacing={0} justify="flex-start">
                          <Item xs={11} justify="flex-start">
                            <Text>Label</Text>
                            <TextField
                              small
                              value={name}
                              required
                              placeholder="Choisissez un label"
                              onChange={handleChange('name')}
                            />
                          </Item>
                        </Row>
                      </Container>
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Organisation" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start">
                      <ListField
                        options={groupsList.map(item => ({
                          _id: item?._id,
                          name: item?.name,
                          activatedAudiences: item?.activatedAudiences
                        }))}
                        searchOptionsKeys={['name', '_id']}
                        values={
                          startGroup?.map(item => ({
                            _id: item?._id,
                            name: item?.name,
                            activatedAudiences: item?.activatedAudiences
                          })) || []
                        }
                        onChangeInput={value => {
                          pollGroups(value);
                        }}
                        onChange={handleChangeGroup}
                        actionTxt="Ajouter une organisation"
                        deleteAction
                        variant="asyncAutoComplete"
                        hideKeys={['activatedAudiences']}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
        </Container>
      </Loading>
    </Modal>
  );
};

ModalForm.defaultProps = {
  config: {},
  actionBeta: null,
  update: false
};
ModalForm.propTypes = {
  onValidate: PropTypes.func.isRequired,
  actionBeta: PropTypes.string,
  config: PropTypes.objectOf(PropTypes.any),
  update: PropTypes.bool
};

export default ModalForm;
